import {
	Col,
	Form,
	Input,
	InputRef,
	RefSelectProps,
	Row,
	Select,
	notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { ApprovalLimitField } from 'components/ApprovalLimitField';
import { isApprovalLimitInvalid, parseBRLToNumber } from 'helpers/currency';
import { UserCreationFormConfirmButton, UserCreationFormItem } from './styles';
import { cpfRule } from 'helpers/rules';
import { normalizeCpf } from 'helpers/normalizers';
import { cleanDocument } from 'helpers/documentUtils';
import { User, UserDto } from 'types/User';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from 'types/ApiError';
import { UsersService } from 'modules/users/services';
import { AccessService } from 'modules/users/services/access';
import {
	AccessDto,
	AccessListContent,
	AccessPermission,
	AccessType,
} from 'types/Access';
import { queryClient } from 'network/query';
import { useCurrentProfile } from 'modules/core/context/ProfileContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	AccessPermissionDescription,
	AccessPermissionRestricted,
} from 'constants/access';
import FormItem from 'antd/es/form/FormItem';
import { validateCpf } from 'helpers/validators';
import { useUser } from 'modules/users/hooks';
import { Modal } from 'components/Modal';
import { TextM, TextS } from 'components/Text';
import { FaCheck } from 'react-icons/fa6';
import { useTheme } from 'styled-components';
import { isSandbox } from 'helpers/environment';

type UserCreationDto = UserDto & {
	permissions?: AccessPermission[];
	postingApprovalLimit?: number;
	postingApprovalLimitActive?: boolean;
};
export type UserEditionDto = User & { permissions?: AccessPermission[] };

interface IUserCreationProps {
	isOpen: boolean;
	companyId?: string;
	user?: AccessListContent;
	onClose: () => void;
	onSuccess: () => void;
}
const UserCreation = ({
	isOpen,
	companyId,
	user: editUser,
	onClose,
	onSuccess,
}: IUserCreationProps) => {
	const theme = useTheme();
	const [form] = Form.useForm();
	const emailAddressRef = useRef<InputRef>(null);
	const permissionsRef = useRef<RefSelectProps>(null);
	const [api, contextHolder] = notification.useNotification();
	const userProfile = useCurrentProfile();

	const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);

	const navigate = useNavigate();
	const [existingAccess, setExistingAccess] = useState<
		AccessListContent | undefined
	>();

	const targetPersonId =
		companyId ??
		(userProfile.type === AccessType.PERSON
			? userProfile.person?.id
			: undefined);

	const blocked = typeof editUser !== 'undefined' || !!existingAccess;

	const currentPermissionsList =
		userProfile.type !== AccessType.FINANCIAL_INSTITUTION
			? userProfile.permissions
			: Object.values(AccessPermission);

	const isCreatingCompanyUser =
		userProfile.type !== AccessType.FINANCIAL_INSTITUTION ||
		typeof companyId === 'string';

	// limite de aprovação (só p/ acesso PERSON com POSTING_REVIEW selecionado)
	const [postingApprovalLimitActive, setPostingApprovalLimitActive] =
		useState<boolean>(false);
	const [postingApprovalLimit, setPostingApprovalLimit] = useState<
		string | number
	>(0);

	const watchedPermissions = Form.useWatch('permissions', form) as
		| AccessPermission[]
		| undefined;
	const watchedTaxpayerId = Form.useWatch('taxpayer_id', form);
	const watchedEmail = Form.useWatch('email_address', form);
	const watchedFullName = Form.useWatch('full_name', form);
	const missingRequired =
		!watchedTaxpayerId || !watchedEmail || !watchedFullName;
	const showLimit =
		isCreatingCompanyUser &&
		!blocked &&
		!!watchedPermissions?.includes(AccessPermission.POSTING_REVIEW);
	const limitInvalid = isApprovalLimitInvalid(
		postingApprovalLimitActive,
		postingApprovalLimit,
	);

	useEffect(() => {
		if (isOpen) {
			setExistingAccess(undefined);
			if (typeof editUser !== 'undefined') {
				form.setFieldValue(
					'email_address',
					editUser.user.email_address,
				);
				form.setFieldValue(
					'taxpayer_id',
					normalizeCpf(editUser.user.taxpayer_id),
				);
				form.setFieldValue('full_name', editUser.user.full_name);
				form.setFieldValue('permissions', editUser.permissions);
				setPostingApprovalLimitActive(
					editUser.posting_approval_limit_active,
				);
				setPostingApprovalLimit(editUser.posting_approval_limit);
				setTimeout(() => permissionsRef.current?.focus(), 100);
			} else {
				form.setFieldValue('email_address', undefined);
				form.setFieldValue('taxpayer_id', undefined);
				form.setFieldValue('full_name', undefined);
				form.setFieldValue('permissions', undefined);
				setPostingApprovalLimitActive(false);
				setPostingApprovalLimit(0);
			}
		}
	}, [isOpen]);

	const {
		getUserByTaxpayerId,
		user: loadedUser,
		loading,
	} = useUser({
		onError: e => {
			api.error({
				description: e.data?.message,
				message:
					'Ocorreu um problema ao buscar informações do usuário.',
			});
		},
		onSuccess: async (user, hasPrevUser) => {
			if (!user) {
				if (hasPrevUser) {
					form.resetFields([
						'email_address',
						'full_name',
						'permissions',
					]);
				}
				setExistingAccess(undefined);
				setIsFormDisabled(false);
				setTimeout(() => emailAddressRef.current?.focus(), 100);
				return;
			}

			form.setFieldValue('email_address', user.email_address);
			form.setFieldValue('full_name', user.full_name);
			setTimeout(() => permissionsRef.current?.focus(), 100);
			setIsFormDisabled(true);

			try {
				const list = await AccessService.getAccessList({
					taxpayer_id: user.taxpayer_id,
				});
				const match = list.content.find(access =>
					targetPersonId
						? access.person?.id === targetPersonId
						: access.type === AccessType.FINANCIAL_INSTITUTION,
				);
				setExistingAccess(match);
			} catch {
				setExistingAccess(undefined);
			}
		},
	});

	const redirectUserId =
		editUser?.user.id ?? existingAccess?.user?.id ?? loadedUser?.id;

	const createAccessDto = useCallback(
		(
			permissions: AccessPermission[],
			limit?: { value: number; active: boolean },
		): AccessDto => {
			const personDto = (personId?: string): AccessDto => ({
				type: AccessType.PERSON,
				personId,
				permissions,
				postingApprovalLimit: limit?.value ?? 0,
				postingApprovalLimitActive: limit?.active ?? false,
			});
			if (userProfile.type === AccessType.PERSON) {
				return personDto(userProfile.person?.id);
			}
			if (typeof companyId === 'string') {
				return personDto(companyId);
			}
			return {
				type: AccessType.FINANCIAL_INSTITUTION,
				permissions,
			};
		},
		[userProfile, companyId],
	);

	const { mutate, isPending } = useMutation<
		{ id: string },
		ApiError,
		UserCreationDto
	>({
		mutationFn: async userCreationDto => {
			try {
				const {
					permissions,
					postingApprovalLimit: limitValue,
					postingApprovalLimitActive: limitActive,
					...userDto
				} = userCreationDto;
				const limit = {
					value: limitValue ?? 0,
					active: limitActive ?? false,
				};
				if (editUser || loadedUser) {
					const user = editUser?.user ?? loadedUser;
					await AccessService.createAccess(
						user!.id,
						createAccessDto(permissions ?? [], limit),
					);
					return {
						id: user!.id,
					};
				}
				const user = await UsersService.createUser(userDto);
				await AccessService.createAccess(
					user.id,
					createAccessDto(permissions ?? [], limit),
				);
				return {
					id: user!.id,
				};
			} catch (e) {
				return Promise.reject(e);
			}
		},
		onSuccess: () => {
			queryClient.refetchQueries({
				queryKey: ['userList'],
			});

			queryClient.refetchQueries({
				queryKey: ['accessList', companyId],
			});
			onSuccess();
		},
		onError: e => {
			api.error({
				description: e.data.message,
				message: 'Ocorreu um problema ao criar a usuário.',
			});
		},
	});

	return (
		<Modal
			title="Criação de Usuário"
			isOpen={isOpen}
			onClose={onClose}
			width={520}
		>
			{contextHolder}
			{isOpen && (
				<>
					{blocked && (
						<div
							style={{
								background: theme.primaryOpacity,
								borderRadius: '6px',
								padding: '12px 16px',
								marginBottom: '1rem',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '12px',
							}}
						>
							<TextS color={theme.textGray}>
								Esse usuário já possui acesso configurado.
							</TextS>
							{redirectUserId && (
								<TextS
									color={theme.primary}
									weight="bold"
									onClick={() =>
										navigate(`/users/${redirectUserId}`)
									}
									style={{
										cursor: 'pointer',
										whiteSpace: 'nowrap',
									}}
								>
									Ver acesso
								</TextS>
							)}
						</div>
					)}
					<Form<UserCreationDto>
						form={form}
						validateTrigger="onBlur"
						style={{ width: '100%' }}
						requiredMark={false}
						disabled={isFormDisabled || blocked}
						initialValues={{
							taxpayer_id:
								editUser?.user?.taxpayer_id &&
								normalizeCpf(editUser.user.taxpayer_id),
							full_name: editUser?.user.full_name,
							email_address: editUser?.user.email_address,
							permissions: editUser?.permissions,
						}}
						layout="vertical"
						onFinish={values => {
							mutate({
								...values,
								taxpayer_id: cleanDocument(values.taxpayer_id),
								postingApprovalLimit: showLimit
									? parseBRLToNumber(postingApprovalLimit)
									: 0,
								postingApprovalLimitActive: showLimit
									? postingApprovalLimitActive
									: false,
							});
						}}
					>
						<Row gutter={[16, 16]}>
							<Col span={24}>
								<FormItem
									shouldUpdate={(prevValues, currentValues) =>
										prevValues.taxpayer_id !==
										currentValues.taxpayer_id
									}
								>
									{({ getFieldValue }) => {
										return (
											<UserCreationFormItem
												name="taxpayer_id"
												label="Identificação do usuário"
												normalize={value =>
													normalizeCpf(value)
												}
												rules={[
													{
														required: true,
														message:
															'Campo obrigatório',
													},
													cpfRule,
												]}
											>
												<Input
													disabled={
														loading ||
														editUser !== undefined
													}
													placeholder="CPF"
													autoFocus
													onBlur={() => {
														const taxpayerId =
															getFieldValue(
																'taxpayer_id',
															);
														if (
															validateCpf(
																taxpayerId,
															) &&
															!loading
														) {
															getUserByTaxpayerId(
																cleanDocument(
																	taxpayerId,
																),
															);
														}
													}}
												/>
											</UserCreationFormItem>
										);
									}}
								</FormItem>
							</Col>
						</Row>
						<Row gutter={[16, 16]} style={{ marginTop: '0.6rem' }}>
							<Col span={24}>
								<UserCreationFormItem
									name="email_address"
									label="E-mail do usuário"
									rules={[
										{
											required: true,
											message: 'Campo obrigatório',
										},
										{
											type: 'email',
											message: 'E-mail inválido',
										},
									]}
								>
									<Input
										ref={emailAddressRef}
										placeholder="Ex.: exemplo@exemplo.com"
									/>
								</UserCreationFormItem>
							</Col>
						</Row>
						<Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
							<Col span={24}>
								<UserCreationFormItem
									name="full_name"
									label="Nome do usuário"
									rules={[
										{
											required: true,
											message: 'Campo obrigatório',
										},
									]}
								>
									<Input placeholder="Ex.: João da Silva" />
								</UserCreationFormItem>
							</Col>
						</Row>
						{isCreatingCompanyUser && (
							<Row
								gutter={[16, 16]}
								style={{ marginTop: '1rem' }}
							>
								<Col span={24}>
									<UserCreationFormItem
										name="permissions"
										label="Permissões"
									>
										<Select
											mode="multiple"
											disabled={false}
											ref={permissionsRef}
											style={{ minWidth: '100%' }}
											placeholder="Selecione as permissões"
											options={currentPermissionsList
												.map(key => ({
													label: AccessPermissionDescription[
														key as AccessPermission
													],
													value: key,
												}))
												.filter(
													permission =>
														isSandbox() ||
														!AccessPermissionRestricted.includes(
															permission.value as AccessPermission,
														),
												)}
										/>
									</UserCreationFormItem>
								</Col>
							</Row>
						)}
						{showLimit && (
							<Row
								gutter={[16, 16]}
								style={{ marginTop: '1rem' }}
							>
								<Col span={24}>
									<ApprovalLimitField
										value={postingApprovalLimit}
										active={postingApprovalLimitActive}
										onChangeValue={setPostingApprovalLimit}
										onChangeActive={
											setPostingApprovalLimitActive
										}
									/>
								</Col>
							</Row>
						)}
						<Row gutter={[16, 16]} style={{ marginTop: 'auto' }}>
							<Col span={24}>
								<UserCreationFormConfirmButton
									type="primary"
									loading={isPending}
									disabled={
										(showLimit && limitInvalid) ||
										blocked ||
										missingRequired
									}
									htmlType="submit"
								>
									<TextM>Criar acesso</TextM>
									<FaCheck color={theme.white} />
								</UserCreationFormConfirmButton>
							</Col>
						</Row>
					</Form>
				</>
			)}
		</Modal>
	);
};

export default UserCreation;
